import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { AuthMenuItem } from '../components/authMenuItem';
import RoleSwitcher from '../components/RoleSwitcher';
import { selectActiveRole } from '../slices/userRoleSlice';
import { getAvailableFeatures, getMenuIdForFeature } from './roleUtils';

const VerticalSidebar = ({ animation, direction, visible, callBackonClick }) => {
  const activeRole = useSelector(selectActiveRole) || 'admin'; // Default to admin if no role is set

  // Get the available features for the active role
  const availableFeatures = getAvailableFeatures(activeRole);

  // Define menu items for each role
  const getMenuItems = () => {
    // Common items for all roles
    const commonItems = [
      { title: "Book Details", iconName: "book", menuId: 'BOOK-DETAILS', feature: 'bookDetails' },
      { title: "Literary Group Builder", iconName: "file text", menuId: 'TEXT-LIBRARY', feature: 'textLibrary' },
    ];

    // Admin-specific items
    const adminItems = [
      ...commonItems,
      { title: "Voice Actors", iconName: "users", menuId: 'VOICE-ACTORS', feature: 'voiceActors' },
      { title: "Voice Lab", iconName: "lab", menuId: 'VOICE-LAB', feature: 'voiceLab' },
      { title: "Voice Lab My Recordings", iconName: "microphone", menuId: 'VOICE-LAB-MY-RECORDINGS', feature: 'voiceLabMyRecordings' },
      { title: "Voice Lab Integration Demo", iconName: "lab", menuId: 'VOICE-LAB-DEMO', feature: 'voiceLabDemo' },
      { title: "Voice Sample Worksheet", iconName: "volume up", menuId: 'VOICE-SAMPLE-WORKSHEET', feature: 'VoiceSampleWorksheet' },
      { title: "Book Creator Worksheet", iconName: "pencil", menuId: 'BOOK-CREATOR-WORKSHEET', feature: 'bookCreatorWorksheet' },
      { title: "Voice Actor Worksheet", iconName: "edit outline", menuId: 'VOICE-ACTOR-WORKSHEET-ENH', feature: 'voiceActorWorksheetEnh' },
      { title: "Voice Library", iconName: "microphone", menuId: 'VOICE-LIBRARY', feature: 'voiceLibrary' },
      { title: "Audio Library", iconName: "edit", menuId: 'AUDIO-LIBRARY', feature: 'audioLibrary' },
      { title: "Story Importer", iconName: "file audio outline", menuId: 'STORY-IMPORTER', feature: 'storyImporter' },
      { title: "AI File Editor", iconName: "lab", menuId: 'AI-FILE-LAB', feature: 'aiFileLab' },
      { title: "Admin", iconName: "", menuId: '', showDivider: true, feature: 'adminDivider' },
      { title: "Admin Article Editor", iconName: "file audio outline", menuId: 'ADMIN-ARTICLE-EDITOR', feature: 'adminArticleEditor' },
      { title: "Admin AI Query Editor", iconName: "eye", menuId: 'ADMIN-AI-QUERY-EDITOR', feature: 'adminAiQueryEditor' },
      { title: "System Management", iconName: "settings", menuId: 'USER-MANAGEMENT', feature: 'userManagement' },
      { title: "Messaging Test", iconName: "comments", menuId: 'MESSAGING-TEST', feature: 'messagingTest' },
      // Phase 4d Step 3 — admins also need access for support / debugging.
      { title: "Rendering Studio", iconName: "play circle", menuId: 'RENDERING-STUDIO', feature: 'renderingStudio' }
    ];

    // Book Creator-specific items
    const bookCreatorItems = [
      ...commonItems,
      { title: "Voice Actors", iconName: "users", menuId: 'VOICE-ACTORS', feature: 'voiceActors' },
      { title: "Book Creator Worksheet", iconName: "pencil", menuId: 'BOOK-CREATOR-WORKSHEET', feature: 'bookCreatorWorksheet' },
      { title: "Audio Library", iconName: "edit", menuId: 'AUDIO-LIBRARY', feature: 'audioLibrary' },
      { title: "Story Importer", iconName: "file audio outline", menuId: 'STORY-IMPORTER', feature: 'storyImporter' },
      { title: "AI File Editor", iconName: "lab", menuId: 'AI-FILE-LAB', feature: 'aiFileLab' },
      { title: "Voice Actor Assignments", iconName: "tasks", menuId: 'VOICE-ACTOR-ASSIGNMENTS', feature: 'voiceActorAssignments' },
      // Phase 4d Step 3 — Rendering Studio R1. Sister entry-point to the
      // landing-page quick-action card; menuId routes through the Dashboard
      // shell so the BC sidebar stays visible while they eye-test grids.
      { title: "Rendering Studio", iconName: "play circle", menuId: 'RENDERING-STUDIO', feature: 'renderingStudio' }
    ];

    // Voice Actor-specific items
    const voiceActorItems = [
      { title: "My Activity History", iconName: "tasks", menuId: 'MY-ASSIGNMENTS', feature: 'myAssignments' },
      { title: "My Portfolio", iconName: "folder", menuId: 'MY-PORTFOLIO', feature: 'myPortfolio' },
      // REMOVED: Voice Actor Worksheet - functionality consolidated into My Portfolio and Sample Manager
      // ARCHIVED: Voice Lab, Voice Lab My Recordings, Work in Progress — removed as part of legacy cleanup
    ];

    // Return the appropriate items based on the active role
    switch (activeRole) {
      case 'admin':
        return adminItems;
      case 'bookCreator':
        return bookCreatorItems;
      case 'voiceActor':
        return voiceActorItems;
      default:
        return commonItems;
    }
  };

  // Get the menu items for the active role
  const menuItems = getMenuItems();

  // Filter menu items based on available features
  const filteredMenuItems = menuItems.filter(item =>
    item.showDivider || availableFeatures.includes(item.feature)
  );

  return (
    <div className='dashBoardSidebar-menuList-outer'>
      <div className='dashMenuTitle dashMenuRoleSwitcher' >
        <RoleSwitcher location="dashboard" />
      </div>
      <div className='dashMenuTitle'>Projects</div>
      {filteredMenuItems.map((item, index) => (
        <AuthMenuItem
          key={index}
          title={item.title}
          iconName={item.iconName}
          instanceCount="5"
          callBackonClick={() => {
            console.log('menu item', item)
            return item.menuId ? callBackonClick(item.menuId) : null;
          }}
          showDivider={item.showDivider}
        />
      ))}
    </div>
  );
};

function DashBoardSideBar(menuSelection) {
  const [menuSel, setMenuSel] = useState("TEXT-LIBRARY");
  const menuSelCB = useCallback((value) => {
    console.log('DashBoardSideBar menuSel', menuSel)

    setMenuSel(value);
    menuSelection(value);
  }, [menuSelection]);
  return (
    <div>
      <VerticalSidebar
        selected={menuSel}
        animation={'overlay'}
        direction={'left'}
        visible={true}
        callBackonClick={menuSelCB}
      />
    </div>
  );
}

export default DashBoardSideBar;
